import React from 'react';
import {
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  useGetOne,
  useNotify,
} from 'react-admin';

import { useFormContext } from 'react-hook-form';

import {
  Box,
  Typography,
  useTheme,
  Button,
  Card,
} from '@mui/material';

import baseRoles, { BaseRoles, baseRolesChoices } from '../../constants/baseRoles';
import InfoPopover from '../layout/InfoPopover';
import { PermissionAccordionCreate } from './PermissionAccordion/PermissionAccordion';
import { ReformedPermissionRole } from './type';
import { Actions } from '../../constants/actions';
import resourceNames from '../../constants/resourceNames';
import PermissionRoleIconInput from './PermissionRoleIconInput';

const CustomToolbar = ({
  onCancel,
}: {
  onCancel: () => void;
}) => {
  const { formState: { isValid } } = useFormContext();

  return (
    <Box
      display="flex"
      alignItems="stretch"
      width="100%"
      justifyContent="flex-end"
      gap={2}
    >
      <Button
        onClick={onCancel}
        variant="outlined"
      >
        Cancel
      </Button>
      <SaveButton
        disabled={!isValid}
        icon={undefined}
      />
    </Box>
  );
};

const PermissionTemplate = () => {
  const { setValue } = useFormContext();
  const { data } = useGetOne<{id: string; defaultPermissionRoles: Record<Exclude<BaseRoles, 'none'>, Actions[]>}>(resourceNames.PERMISSION_ROLES, { id: 'default-permission-roles' });
  const notify = useNotify();

  const handleChange = (permissionTemplate: BaseRoles) => {
    const transformedData: {
      permissions: Partial<Record<Actions, {
        enabled: boolean;
        attributes: unknown;
      }>>; } | undefined = (data && permissionTemplate !== baseRoles.NONE) ? {
      permissions: (data.defaultPermissionRoles[permissionTemplate] ?? [])
        .reduce((acc, name) => ({
          ...acc,
          [name]: {
            enabled: true,
            attributes: undefined,
          },
        }), {}),
    } : undefined;
    setValue('permissions', transformedData?.permissions);
    setValue('baseRole', permissionTemplate);
    notify('Permissions prefilled.', { type: 'success' });
  };

  return (
    <SelectInput
      source="permissionTemplate"
      choices={baseRolesChoices}
      helperText={false}
      defaultValue={baseRolesChoices.at(0)?.id}
      onChange={(e) => handleChange(e.target.value)}
      validate={required()}
    />
  );
};

const PermissionRolesForm = ({
  mode,
  onCancel,
  transformedData,
}: {
  mode: 'edit' | 'create';
  onCancel: () => void;
  transformedData?: ReformedPermissionRole;
}) => {
  const theme = useTheme();

  return (
    <Card variant="outlined" sx={{ padding: 4 }}>
      <SimpleForm
        sx={{
          '& > .MuiStack-root': {
            alignItems: 'stretch',
          },
        }}
        component={Box}
        mode="all"
        record={transformedData ?? {}}
        toolbar={<CustomToolbar onCancel={onCancel} />}
      >
        <Box display="flex" flexDirection="column" gap={4}>
          <Box display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h4" color="primary" marginBottom={4}>1. Name</Typography>
              {mode === 'edit' && (
                <Typography paddingY={1} paddingX={2.5} sx={{ backgroundColor: '#FFD54F', borderRadius: 1 }}>Editing</Typography>
              )}
            </Box>
            <Box display="flex" alignItems="flex-start">
              <TextInput source="name" label="Role name" color="primary" validate={required()} />
            </Box>
            <TextInput source="description" fullWidth label="Role description" color="primary" multiline />
            <PermissionRoleIconInput source="iconName" label="Choose an icon for the role" />
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="h4" color="primary" marginBottom={4}>2. Select permissions</Typography>
            {mode === 'create' && (
              <Box
                bgcolor={theme.palette.greenGray.main}
                borderRadius={1}
                marginBottom={4}
              >
                <Box display="flex" alignItems="center">
                  <Typography>Permission template</Typography>
                  <InfoPopover
                    content="Copy permissions from an existing role to get started faster."
                    title="Templates"
                  />
                </Box>
                <PermissionTemplate />
              </Box>
            )}
            <PermissionAccordionCreate />
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="h4" color="primary" marginBottom={4}>3. Feature updates</Typography>
            <Box display="flex" alignItems="center">
              <Typography>Base role</Typography>
              <InfoPopover
                content="Custom roles stay aligned with new product features by selecting a Base Role. When new actions become available, Pingwire updates the Base Role's permissions and any custom roles based on it will automatically receive those same updates."
                title="Base role"
              />
            </Box>
            <Box display="flex" alignItems="flex-start">
              <SelectInput
                source="baseRole"
                choices={baseRolesChoices}
                defaultValue={baseRolesChoices.at(0)?.id}
                validate={required()}
              />
            </Box>
          </Box>
        </Box>
      </SimpleForm>
    </Card>
  );
};

export default PermissionRolesForm;
